import React, { useState, useEffect } from "react";
import { RiRefreshLine } from "react-icons/ri";
import { Button } from "antd";
import styles from "./login.module.scss";

interface GraphicValidatorProps {
  generateVerifyCode(): void;
  verifyCode: string;
}

const GraphicValidator = React.memo(({ generateVerifyCode, verifyCode }: GraphicValidatorProps) => {
  const [codeSetting] = useState({
    // 驗證碼的樣式設定，如字體大小、顏色、背景顏色等。
    // 這些值在整個組件生命週期中保持不變。
    fontSizeMin: 42,
    fontSizeMax: 30,
    backgroundColorMin: 180,
    backgroundColorMax: 240,
    colorMin: 50,
    colorMax: 60,
    lineColorMin: 30,
    lineColorMax: 180,
    dotColorMin: 0,
    dotColorMax: 255,
    contentWidth: 200,
    contentHeight: 55,
  });
  // 當 verifyCode 發生變化時（每次生成新的驗證碼時），會調用 drawPic 函數重新繪製圖像。
  useEffect(() => {
    drawPic();
  }, [verifyCode]);

  //生成指定範圍內的隨機整數
  const randomNum = (min: number, max: number) => Math.floor(Math.random() * (max - min) + min);
  //生成指定範圍內的隨機顏色
  const randomColor = (min: number, max: number) => {
    const r = randomNum(min, max);
    const g = randomNum(min, max);
    const b = randomNum(min, max);
    return `rgb(${r},${g},${b})`;
  };

  //產生圖片文字，繪製文字到 Canvas 上
  const drawText = (ctx: CanvasRenderingContext2D, txt: string, i: number) => {
    ctx.fillStyle = randomColor(codeSetting.colorMin, codeSetting.colorMax);
    ctx.font = `${randomNum(codeSetting.fontSizeMin, codeSetting.fontSizeMax) * 1.2}px SimHei`;
    const x = (i + 1) * (160 / (verifyCode.length + 1));
    const y = randomNum(codeSetting.fontSizeMax, codeSetting.contentHeight - 5) + 10;
    const deg = randomNum(-25, 25);

    // 修改座標原點及角度
    ctx.translate(x, y);
    ctx.rotate((deg * Math.PI) / 180);
    ctx.fillText(txt, 10, 0);
    // 恢复座標原點及角度
    ctx.rotate((-deg * Math.PI) / 180);
    ctx.translate(-x, -y);
  };

  //繪製干擾點
  const drawDot = (ctx: CanvasRenderingContext2D) => {
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = randomColor(0, 255);
      ctx.beginPath();
      ctx.arc(randomNum(0, codeSetting.contentWidth), randomNum(0, codeSetting.contentHeight), 1, 0, 2 * Math.PI);
      ctx.fill();
    }
  };

  //繪製干擾線
  const drawLine = (ctx: CanvasRenderingContext2D) => {
    for (let i = 0; i < 8; i++) {
      ctx.strokeStyle = randomColor(codeSetting.lineColorMin, codeSetting.lineColorMax);
      ctx.beginPath();
      ctx.moveTo(randomNum(0, window.innerWidth), randomNum(0, codeSetting.contentHeight));
      ctx.lineTo(randomNum(0, codeSetting.contentWidth), randomNum(0, codeSetting.contentHeight));
      ctx.stroke();
    }
  };

  //繪製最終圖像
  const drawPic = () => {
    // 獲取 canvas 元素和 2D 上下文
    const canvas = document.getElementById("s-canvas") as HTMLCanvasElement;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("ctx not exist");
    }

    ctx.textBaseline = "bottom";
    // 繪製背景
    ctx.fillStyle = randomColor(codeSetting.backgroundColorMin, codeSetting.backgroundColorMax);
    ctx.fillRect(0, 0, window.innerWidth, codeSetting.contentHeight);
    // 繪製文字
    for (let i = 0; i < verifyCode.length; i++) {
      drawText(ctx, verifyCode[i], i);
    }
    drawLine(ctx);
    drawDot(ctx);
  };

  // 這裡顯示了一個包含 Canvas 元素、刷新按鈕以及相應的樣式的 div。
  // 單擊整個區域（div）將調用 generateVerifyCode 函數生成新的驗證碼。
  return (
    <div onClick={generateVerifyCode} className={styles.login__captcha}>
      <canvas height={codeSetting.contentHeight} id="s-canvas" />
      <RiRefreshLine className={styles.login__captcha__icon} />
    </div>
  );
});

export default GraphicValidator;
